import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  memo,
  useState,
} from 'react';
import type { FormEvent } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  ENTER,
  ESCAPE,
} from '@ipfabric/ui-components/utils/keyboard/constants';
import type { TopologyGraphPreset } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';

interface Props {
  onSaveClick: (name: TopologyGraphPreset['name']) => void;
  onCloseClick: () => void;
  saving?: boolean;
}

const BareGraphAddPresetInput: FC<Props> = (props) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const {
    canvasGraph: {
      graphSidePanel: {
        layoutSettings: { presetsControls: presetsControlsMsg },
      },
    },
  } = useLocale();

  const handleClose = useStableCallback(() => {
    props.onCloseClick();
  });

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    if (!inputValue) {
      setHasError(true);
    } else {
      props.onSaveClick(inputValue);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (hasError) {
      setHasError(false);
    }
  };

  const handleKeyDown = useStableCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.which === ESCAPE) {
        handleClose();
      }
      if (e.which === ENTER) {
        handleSave(e);
      }
    },
  );

  return (
    <div className='relative w-full'>
      <TextInput
        autoFocus
        hasError={hasError}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={presetsControlsMsg.addPresetPlaceholder}
        suffixText={
          <span className='whitespace-nowrap'>
            <Button
              variant='tertiary'
              icon='fa-check'
              size='xs'
              type='submit'
              onClick={handleSave}
              loading={props.saving}
            />
            <Button
              variant='tertiary'
              icon='fa-xmark'
              size='xs'
              type='submit'
              onClick={handleClose}
              disabled={props.saving}
            />
          </span>
        }
        value={inputValue}
        type='text'
      />
    </div>
  );
};

export const GraphAddPresetInput = memo(BareGraphAddPresetInput);
